import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";
import { FaChevronLeft, FaChevronRight, FaPlus, FaCheck, FaTimes, FaPalette } from "react-icons/fa";
import { Workspace } from "@/models/account";
import { useCreateWorkspace, useUpdateWorkspace } from "@/api/account";
import { useQueryClient } from "@tanstack/react-query";
import { useWorkspaceTheme } from "@/hooks/use-workspace-theme";
import ColorPalettePicker from "./color-palette-picker";
import { MotionButton, MotionDiv, MotionSpan } from "./motion-types";
import logger from "@/utils/logger";
import { Z_INDEX, zIndexStyle } from "@/utils/z-index";

interface WorkspaceSliderProps {
  workspaces: Workspace[];
  currentIndex: number;
  onSelect: (index: number) => void;
  isPending?: boolean;
}

const WorkspaceSlider: React.FC<WorkspaceSliderProps> = ({
  workspaces,
  currentIndex,
  onSelect,
  isPending = false,
}) => {
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const colorButtonRef = useRef<HTMLButtonElement>(null);
  const selectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const createWorkspace = useCreateWorkspace();
  const updateWorkspace = useUpdateWorkspace();
  const queryClient = useQueryClient();
  const { derivedColors } = useWorkspaceTheme();
  
  // Use the current workspace's color from props, not from global theme
  // This ensures we display and update the correct workspace's color
  const currentWorkspaceColor = workspaces[currentIndex]?.theme_config?.colors?.[0]?.color ?? derivedColors.accent;
  
  // Total pages = workspaces + 1 (for create new)
  const totalPages = workspaces.length + 1;
  const isOnCreatePage = currentIndex === workspaces.length;
  
  // Sidebar is always dark (night mode), so use light text colors
  const borderColor = "border-white/10";
  const textColor = "text-white";
  const textMutedColor = "text-white/60";
  const textDisabledColor = "text-white/20";
  const bgHoverColor = "hover:bg-white/10";

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      onSelect(currentIndex - 1);
    }
  }, [currentIndex, onSelect]);

  const goToNext = useCallback(() => {
    if (currentIndex < totalPages - 1) {
      onSelect(currentIndex + 1);
    }
  }, [currentIndex, totalPages, onSelect]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (selectTimeoutRef.current) {
        clearTimeout(selectTimeoutRef.current);
      }
    };
  }, []);

  if (workspaces.length === 0) {
    return null;
  }

  const currentWorkspace = workspaces[currentIndex];

  // Focus input when creating workspace
  useEffect(() => {
    if (isCreatingWorkspace && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreatingWorkspace]);

  // Close color picker when clicking outside
  useEffect(() => {
    if (!isColorPickerOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorPickerRef.current && 
        !colorPickerRef.current.contains(event.target as Node) &&
        colorButtonRef.current &&
        !colorButtonRef.current.contains(event.target as Node)
      ) {
        setIsColorPickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isColorPickerOpen]);

  // Open color picker and calculate position
  const handleOpenColorPicker = useCallback(() => {
    if (colorButtonRef.current) {
      const rect = colorButtonRef.current.getBoundingClientRect();
      setPopoverPosition({
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
    }
    setIsColorPickerOpen((prev) => !prev);
  }, []);

  // Handle color change for current workspace
  const handleColorChange = useCallback(async (color: string) => {
    if (!currentWorkspace?.id) return;

    // Optimistic update: update cache immediately for instant UI feedback
    const newThemeConfig = {
      mode: "auto" as const,
      colors: [{ id: "1", color, x: 50, y: 50 }],
    };

    // Update only the workspaces list query (not individual workspace queries)
    // The query key format is [serializedConfig, accessToken] where serializedConfig is JSON with url field
    queryClient.setQueriesData<Workspace[]>(
      {
        predicate: (query) => {
          const key = query.queryKey[0];
          if (typeof key !== "string") return false;
          try {
            const config = JSON.parse(key);
            // Only match the workspaces list endpoint, not individual workspace queries
            return config.url === "/workspaces/";
          } catch {
            return false;
          }
        },
      },
      (oldData) => {
        if (!oldData) return oldData;
        return oldData.map((ws) =>
          ws.id === currentWorkspace.id
            ? { ...ws, theme_config: newThemeConfig }
            : ws
        );
      }
    );

    setIsColorPickerOpen(false);

    try {
      await updateWorkspace.mutateAsync({
        uriParams: { id: currentWorkspace.id },
        theme_config: newThemeConfig,
      });
      // Refetch to ensure we have the server's version
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          if (typeof key !== "string") return false;
          try {
            const config = JSON.parse(key);
            return config.url === "/workspaces/";
          } catch {
            return false;
          }
        },
      });
    } catch (error) {
      // Rollback on error by refetching
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          if (typeof key !== "string") return false;
          try {
            const config = JSON.parse(key);
            return config.url === "/workspaces/";
          } catch {
            return false;
          }
        },
      });
      logger.error("Failed to update workspace color:", error);
    }
  }, [currentWorkspace?.id, updateWorkspace, queryClient]);

  // Handle create workspace
  const handleCreateWorkspace = useCallback(async () => {
    if (!newWorkspaceName.trim()) return;
    
    try {
      await createWorkspace.mutateAsync({
        name: newWorkspaceName.trim(),
      });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === "string" && key.includes("/workspaces/");
        },
      });
      setNewWorkspaceName("");
      setIsCreatingWorkspace(false);
      // Select the new workspace (it will be at the end of the list)
      selectTimeoutRef.current = setTimeout(() => {
        onSelect(workspaces.length);
        selectTimeoutRef.current = null;
      }, 100);
    } catch (error) {
      logger.error("Failed to create workspace:", error);
    }
  }, [newWorkspaceName, createWorkspace, queryClient, workspaces.length, onSelect]);

  // Handle key press in input
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleCreateWorkspace();
      } else if (e.key === "Escape") {
        setIsCreatingWorkspace(false);
        setNewWorkspaceName("");
      }
    },
    [handleCreateWorkspace]
  );

  return (
    <div 
      className={`border-t ${borderColor} relative`}
    >
      {/* Workspace content */}
      <div className="overflow-hidden px-3 py-3">
        {/* Workspace Name with Navigation */}
        <div className="flex items-center justify-between mb-2">
          <MotionButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={goToPrevious}
            disabled={currentIndex === 0}
            className={`p-1.5 rounded-lg transition-all ${
              currentIndex === 0
                ? `${textDisabledColor} cursor-not-allowed`
                : `${textMutedColor} hover:${textColor} ${bgHoverColor}`
            }`}
          >
            <FaChevronLeft size={12} />
          </MotionButton>

          <AnimatePresence mode="wait">
            {isOnCreatePage ? (
              // Create workspace page
              <MotionDiv
                key="create-page"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{
                  type: "spring",
                  stiffness: 500,
                  damping: 30,
                  mass: 0.8,
                }}
                className="flex-1 flex items-center justify-center"
              >
                {isCreatingWorkspace ? (
                  <div className="flex items-center gap-2 w-full max-w-[180px]">
                    <input
                      ref={inputRef}
                      type="text"
                      value={newWorkspaceName}
                      onChange={(e) => setNewWorkspaceName(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Workspace name"
                      className={`flex-1 px-2 py-1 text-sm rounded-lg border ${borderColor} bg-transparent ${textColor} placeholder:${textMutedColor} focus:outline-none focus:ring-1 focus:ring-[var(--arc-accent)]`}
                    />
                    <MotionButton
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handleCreateWorkspace}
                      disabled={!newWorkspaceName.trim() || createWorkspace.isPending}
                      className={`p-1.5 rounded-lg transition-all ${
                        newWorkspaceName.trim()
                          ? "text-green-500 hover:bg-green-500/10"
                          : `${textMutedColor} cursor-not-allowed`
                      }`}
                    >
                      {createWorkspace.isPending ? (
                        <div 
                          className="w-3 h-3 border-2 rounded-full animate-spin"
                          style={{
                            borderColor: `${derivedColors.accent}30`,
                            borderTopColor: derivedColors.accent,
                          }}
                        />
                      ) : (
                        <FaCheck size={12} />
                      )}
                    </MotionButton>
                    <MotionButton
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => {
                        setIsCreatingWorkspace(false);
                        setNewWorkspaceName("");
                      }}
                      className="p-1.5 rounded-lg transition-all text-red-500 hover:bg-red-500/10"
                    >
                      <FaTimes size={12} />
                    </MotionButton>
                  </div>
                ) : (
                  <MotionButton
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsCreatingWorkspace(true)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl ${bgHoverColor} ${textMutedColor} hover:${textColor} transition-all border border-dashed ${borderColor}`}
                  >
                    <FaPlus size={10} />
                    <span className="text-sm font-medium">New Workspace</span>
                  </MotionButton>
                )}
              </MotionDiv>
            ) : (
              // Workspace name display with color picker
              <MotionDiv
                key={currentWorkspace?.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{
                  type: "spring",
                  stiffness: 500,
                  damping: 30,
                  mass: 0.8,
                }}
                className="flex-1 flex items-center justify-center gap-2 relative"
              >
                {/* Color Picker Button */}
                <MotionButton
                  ref={colorButtonRef}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleOpenColorPicker}
                  className={`p-1.5 rounded-lg transition-all ${textMutedColor} hover:${textColor} ${bgHoverColor}`}
                  title="Change workspace color"
                >
                  <FaPalette size={12} style={{ color: currentWorkspaceColor }} />
                </MotionButton>

                {/* Color Picker Popover - rendered via portal */}
                {isColorPickerOpen && createPortal(
                  <AnimatePresence>
                    <MotionDiv
                      ref={colorPickerRef}
                      initial={{ opacity: 0, y: 8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="fixed p-3 bg-[var(--arc-sidebar-background)] rounded-xl shadow-xl border border-[var(--arc-sidebar-border-accent)]"
                      style={{ 
                        left: popoverPosition.x,
                        top: popoverPosition.y,
                        transform: "translate(-50%, calc(-100% - 8px))",
                        minWidth: "200px",
                        ...zIndexStyle(Z_INDEX.COLOR_PICKER),
                      }}
                    >
                      <ColorPalettePicker
                        value={currentWorkspaceColor}
                        onChange={handleColorChange}
                        compact
                      />
                      {updateWorkspace.isPending && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-xl">
                          <div 
                            className="w-4 h-4 border-2 rounded-full animate-spin"
                            style={{
                              borderColor: `${derivedColors.accent}30`,
                              borderTopColor: derivedColors.accent,
                            }}
                          />
                        </div>
                      )}
                    </MotionDiv>
                  </AnimatePresence>,
                  document.body
                )}

                {/* Workspace Name */}
                <MotionSpan
                  animate={{ opacity: isPending ? 0.6 : 1 }}
                  transition={{ duration: 0.15 }}
                  className={`${textColor} font-medium text-sm truncate max-w-[120px]`}
                >
                  {currentWorkspace?.name || "Workspace"}
                </MotionSpan>

                {/* Loading indicator during transition */}
                <AnimatePresence>
                  {isPending && (
                    <MotionDiv
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <div 
                        className="w-3 h-3 border-2 rounded-full animate-spin"
                        style={{
                          borderColor: `${derivedColors.accent}30`,
                          borderTopColor: derivedColors.accent,
                        }}
                      />
                    </MotionDiv>
                  )}
                </AnimatePresence>
              </MotionDiv>
            )}
          </AnimatePresence>

          <MotionButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={goToNext}
            disabled={currentIndex === totalPages - 1}
            className={`p-1.5 rounded-lg transition-all ${
              currentIndex === totalPages - 1
                ? `${textDisabledColor} cursor-not-allowed`
                : `${textMutedColor} hover:${textColor} ${bgHoverColor}`
            }`}
          >
            <FaChevronRight size={12} />
          </MotionButton>
        </div>
      </div>

      {/* Dots Indicator */}
      <div className="flex items-center justify-center gap-1.5 px-3 pb-2">
        {/* Workspace Dots */}
        {workspaces.map((workspace, index) => (
          <MotionButton
            key={workspace.id}
            onClick={() => onSelect(index)}
            animate={{
              scale: index === currentIndex ? 1.2 : 1,
              backgroundColor:
                index === currentIndex
                  ? isPending
                    ? "rgba(255, 255, 255, 0.7)"
                    : derivedColors.accent
                  : "rgba(255, 255, 255, 0.4)",
            }}
            whileHover={{ scale: 1.3 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            className={`w-2 h-2 rounded-full ${
              index === currentIndex && isPending ? "animate-pulse" : ""
            }`}
            style={{
              boxShadow: index === currentIndex ? `0 0 8px ${derivedColors.sidebarAccentGlow}` : undefined,
            }}
            title={`${workspace.name} (⌘${index + 1})`}
          />
        ))}

        {/* Create new workspace dot */}
        <MotionButton
          onClick={() => onSelect(workspaces.length)}
          animate={{
            scale: isOnCreatePage ? 1.2 : 1,
            backgroundColor: isOnCreatePage
              ? derivedColors.accent
              : "rgba(255, 255, 255, 0.4)",
          }}
          whileHover={{ scale: 1.3 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
          className="w-2 h-2 rounded-full flex items-center justify-center"
          style={{
            boxShadow: isOnCreatePage ? `0 0 8px ${derivedColors.sidebarAccentGlow}` : undefined,
          }}
          title="Create new workspace"
        >
          <FaPlus 
            size={6} 
            className={isOnCreatePage ? "text-white" : "text-white/60"}
          />
        </MotionButton>
      </div>
    </div>
  );
};

WorkspaceSlider.displayName = "WorkspaceSlider";

export default WorkspaceSlider;
